'use client';

import React, { useState, useRef, useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../lib/utils';

export interface TooltipProps {
  /** Trigger element. Tooltip attaches to its bounding box. */
  children: React.ReactElement;
  /** Content shown in the tooltip. Keep short (1-2 sentences). */
  content: React.ReactNode;
  /** Preferred side. Falls back to opposite side if it would clip the viewport. */
  side?: 'top' | 'bottom' | 'left' | 'right';
  /** Delay in ms before showing on hover. Default 300ms. */
  openDelay?: number;
  /** Optional extra classes applied to the tooltip bubble. */
  className?: string;
  /** Force-disable the tooltip (e.g., on mobile). */
  disabled?: boolean;
}

/**
 * Lightweight tooltip — hover or focus to reveal a short text bubble.
 *
 * Not a replacement for Popover — for interactive content use Popover.
 * Tooltip is for purely informational hover hints.
 */
export function Tooltip({
  children,
  content,
  side = 'top',
  openDelay = 300,
  className,
  disabled = false,
}: TooltipProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; side: typeof side } | null>(
    null,
  );
  const triggerRef = useRef<HTMLElement | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tooltipId = useId();

  const computePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const margin = 8;
    const approxWidth = 240;
    const approxHeight = 40;

    let resolvedSide = side;
    let top = 0;
    let left = 0;

    const tryPlace = (s: typeof side) => {
      switch (s) {
        case 'top':
          return {
            top: rect.top - approxHeight - margin,
            left: rect.left + rect.width / 2 - approxWidth / 2,
          };
        case 'bottom':
          return {
            top: rect.bottom + margin,
            left: rect.left + rect.width / 2 - approxWidth / 2,
          };
        case 'left':
          return {
            top: rect.top + rect.height / 2 - approxHeight / 2,
            left: rect.left - approxWidth - margin,
          };
        case 'right':
          return {
            top: rect.top + rect.height / 2 - approxHeight / 2,
            left: rect.right + margin,
          };
      }
    };

    const placement = tryPlace(side);
    top = placement.top;
    left = placement.left;

    // Flip if off-screen
    if (side === 'top' && top < 0) {
      resolvedSide = 'bottom';
      const alt = tryPlace('bottom');
      top = alt.top;
      left = alt.left;
    } else if (side === 'bottom' && top + approxHeight > window.innerHeight) {
      resolvedSide = 'top';
      const alt = tryPlace('top');
      top = alt.top;
      left = alt.left;
    }

    // Clamp horizontally
    left = Math.max(margin, Math.min(left, window.innerWidth - approxWidth - margin));

    setPosition({ top, left, side: resolvedSide });
  };

  const show = () => {
    if (disabled) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      computePosition();
      setIsOpen(true);
    }, openDelay);
  };

  const hide = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(false);
    setPosition(null);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // Clone the child to attach handlers without wrapping
  const child = React.cloneElement(children, {
    ref: (node: HTMLElement | null) => {
      triggerRef.current = node;
      // Preserve existing ref on child if any
      const childAny = children as React.ReactElement & { ref?: React.Ref<HTMLElement> };
      const origRef = childAny.ref;
      if (typeof origRef === 'function') origRef(node);
      else if (origRef && typeof origRef === 'object') {
        (origRef as React.MutableRefObject<HTMLElement | null>).current = node;
      }
    },
    onMouseEnter: (e: React.MouseEvent) => {
      show();
      (children.props as { onMouseEnter?: (e: React.MouseEvent) => void }).onMouseEnter?.(e);
    },
    onMouseLeave: (e: React.MouseEvent) => {
      hide();
      (children.props as { onMouseLeave?: (e: React.MouseEvent) => void }).onMouseLeave?.(e);
    },
    onFocus: (e: React.FocusEvent) => {
      show();
      (children.props as { onFocus?: (e: React.FocusEvent) => void }).onFocus?.(e);
    },
    onBlur: (e: React.FocusEvent) => {
      hide();
      (children.props as { onBlur?: (e: React.FocusEvent) => void }).onBlur?.(e);
    },
    'aria-describedby': isOpen ? tooltipId : undefined,
  } as Record<string, unknown>);

  const bubble =
    isOpen && position && typeof window !== 'undefined'
      ? createPortal(
          <div
            id={tooltipId}
            role="tooltip"
            aria-live="polite"
            // Same stacking bug as the dropdowns (2026-08-26): portalled at z-60, below
            // every dialog (z-[200]+), so a tooltip inside a modal never appeared. 290
            // keeps it above the overlays and just under an open dropdown panel.
            style={{ position: 'fixed', top: position.top, left: position.left, zIndex: 290 }}
            className={cn(
              'max-w-xs rounded-lg bg-slate-900 px-3 py-2 text-xs font-medium text-white shadow-lg',
              'animate-in fade-in-0 zoom-in-95 duration-150',
              className,
            )}
          >
            {content}
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      {child}
      {bubble}
    </>
  );
}
